import { useContext, useEffect, useState } from "react";
import { EFSObject, EFSType } from "../../utils/efsUtils";
import NfsDropdown from "../genericComponents/NfsDropdown/NfsDropdown";
import RadioButtonGroup from "../genericComponents/RadioButton/RadioButtonGroup";

import TrashIcon from "../../assets/trash-can.svg?react";

import "./EFSLineItem.css";
import { AppContext } from "../../Context";
import Checkbox from "../genericComponents/Checkbox/Checkbox";
import InputField from "../genericComponents/InputField/InputField";
import { GetCodedValueAndAlias, projectToWebMercator } from "../../utils/arcgisUtils";
import { isNumber } from "lodash";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type Graphic from "@arcgis/core/Graphic";

interface EFSLineItemProps {
    efsObject: EFSObject;
    setEfsObject: (efsObject: EFSObject) => void;
    efsTemplates: Graphic[];
    extended?: boolean;
}

const EFSLineItem = ({ efsObject, efsTemplates, setEfsObject, extended }: EFSLineItemProps) => {

    const [filteredTemplates, setFilteredTemplates] = useState<Graphic[]>([]);
    const [overrideEfsSuggestion, setOverrideEfsSuggestion] = useState<boolean>(false);

    const updateEfsObject = (newEfsObject: EFSObject, attribute: string, value: any) => {
        setEfsObject({ ...newEfsObject, [attribute]: value });
    }

    const appContext = useContext(AppContext);
    const efsSelectedFeatures = appContext?.efsSelectedFeatures;

    const removeObject = () => {
        efsSelectedFeatures?.remove(efsObject.nfsObject);
    }

    const selectEFSMessage = (newValue: string | null) => {
        updateEfsObject(efsObject, "efsMessage", newValue);
        const selectedTemplate = efsTemplates.find(template => template.attributes.melding_norsk === newValue);
        updateEfsObject(efsObject, "efsTemplate", selectedTemplate);
    }

    const handleSetFromDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = new Date(e.target.value);
        if (isNaN(newDate.getTime())) {
            const updatedEfsObject = { ...efsObject };
            delete updatedEfsObject.efsFromDate; // If the date is invalid, remove it
            setEfsObject(updatedEfsObject);
        } else {
            setEfsObject({ ...efsObject, efsFromDate: newDate });
        }
    }

    const handleSetToDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = new Date(e.target.value);
        if (isNaN(newDate.getTime())) {
            const updatedEfsObject = { ...efsObject };
            delete updatedEfsObject.efsToDate; // If the date is invalid, remove it
            setEfsObject(updatedEfsObject);
        } else {
            setEfsObject({ ...efsObject, efsToDate: newDate });
        }
    }

    const formatKlasse = (klasse: string | number) => {
        try {
            const klasseString = String(klasse);
            if (klasseString.length > 0) {
                return klasseString.split(" - ")[0];
            }
            return klasseString;
        } catch (error) {
            return klasse;
        }
    }

    useEffect(() => {

        if (overrideEfsSuggestion) {
            setFilteredTemplates(efsTemplates);
            return;
        }

        let tempFilteredTemplates = [...efsTemplates];

        if (efsObject.efsType) {
            tempFilteredTemplates = tempFilteredTemplates.filter(template => template.attributes.melding_type === efsObject.efsType);
        }

        if (efsObject.nfsObject.layer?.type === "feature") {
            const nfsLayer = efsObject.nfsObject.layer as FeatureLayer;
            tempFilteredTemplates = tempFilteredTemplates.filter(template => template.attributes.nautisk_objekt_kilde === nfsLayer.layerId);
        }

        setFilteredTemplates(tempFilteredTemplates);

    }, [efsObject.efsType, overrideEfsSuggestion, efsTemplates]);

    useEffect(() => {
        if (!efsObject.nfsObject.geometry) {
            console.warn("No geometry found for EFS object:", efsObject);
            return;
        }
        projectToWebMercator(efsObject.nfsObject.geometry).then((projectedGeometry) => {
            if (!projectedGeometry) {
                console.warn("Failed to project geometry for EFS object:", efsObject);
                return;
            }

            let lat: number | null | undefined = 0;
            let lon: number | null | undefined = 0;
            if (projectedGeometry.type === "point") {
                lat = projectedGeometry.latitude;
                lon = projectedGeometry.longitude;
            } else if (projectedGeometry.type === "polyline" || projectedGeometry.type === "polygon") {
                if (!projectedGeometry.extent || !projectedGeometry.extent.center) {
                    console.warn("Projected geometry does not have a valid extent or center:", projectedGeometry);
                    return;
                }
                lat = projectedGeometry.extent.center.latitude;
                lon = projectedGeometry.extent.center.longitude;
            }

            if (isNumber(lat) && isNumber(lon)) {
                const updatedEfsObject = {
                    ...efsObject,
                    efsLat: lat,
                    efsLon: lon,
                };
                setEfsObject(updatedEfsObject);
            }
        });
    }, []);

    return (
        <tr className="efs-line-item" key={efsObject.nfsObject.attributes.objectid + efsObject.nfsObject.attributes.sjomerke_nr}>
            <td>
                <RadioButtonGroup
                    groupClassName="center"
                    options={[EFSType.P]}
                    selectedIndex={efsObject.efsType === EFSType.P ? 0 : 1}
                    onChange={(selectedIndex) => {
                        updateEfsObject(efsObject, "efsType", selectedIndex === 0 ? EFSType.P : EFSType.T);
                    }}
                />
            </td>
            <td >
                <RadioButtonGroup
                    groupClassName="center"
                    options={[EFSType.T]}
                    selectedIndex={efsObject.efsType === EFSType.T ? 0 : 1}
                    onChange={(selectedIndex) => {
                        updateEfsObject(efsObject, "efsType", selectedIndex === 0 ? EFSType.T : EFSType.P);
                    }}
                />
            </td>
            <td><span>{efsObject.nfsObject.attributes.sjomerke_nr}</span></td>
            <td style={{ minWidth: "300px", maxWidth: "300px" }}><span>{efsObject.nfsObject.attributes.sted}</span></td>
            {extended &&
                <>
                    <td>
                        <InputField
                            inputFieldId={"sea-map-number" + efsObject.nfsObject.attributes.objectid}
                            label=""
                            onChange={(value) => { setEfsObject({ ...efsObject, efsSeaMapId: value }) }}
                            value={efsObject.efsSeaMapId ?? ""}
                            type="text"
                        />
                    </td>
                    <td style={{ maxWidth: "50px" }}>
                        <span style={{ overflow: "hidden", textWrap: "nowrap" }}>{formatKlasse(GetCodedValueAndAlias(efsObject.nfsObject.layer, "klasse", efsObject.nfsObject.attributes.klasse)[1])}</span>
                    </td>
                </>
            }
            <td>
                <input id={"efs-from-date" + efsObject.nfsObject.attributes.objectid} onChange={handleSetFromDate} type="date" />
            </td>
            <td>
                <input id={"efs-to-date" + efsObject.nfsObject.attributes.objectid} onChange={handleSetToDate} type="date" />
            </td>
            <td style={{ minWidth: "300px", maxWidth: "300px" }}>
                <div className="efs-dropdown-container">
                    <div className="flex-row small-gap">
                        <label htmlFor={"efs-message-override" + efsObject.nfsObject.attributes.objectid} className="center flex-row small-gap">
                            Vis alle
                            <Checkbox
                                checked={overrideEfsSuggestion}
                                id={"efs-message-override" + efsObject.nfsObject.attributes.objectid}
                                onChange={() => { setOverrideEfsSuggestion(!overrideEfsSuggestion) }}
                            />
                        </label>
                    </div>
                    <NfsDropdown
                        options={filteredTemplates.map((template) => template.attributes.melding_norsk)}
                        placeholder="Velg EFS"
                        selectedOption={efsObject.efsTemplate ? efsObject.efsTemplate.attributes.melding_norsk : null}
                        setSelectedOption={selectEFSMessage}
                    />
                </div>
            </td>
            <td>
                <span>{efsObject.efsTemplate?.attributes.endring_type}</span>
            </td>
            <td className="actions">
                <button className="empty-button center" style={{ justifySelf: "center" }} onClick={removeObject}>
                    <TrashIcon style={{ width: "28px", height: "28px" }} />
                </button>
            </td>
        </tr>
    );
};

export default EFSLineItem;
